'use client';

import { useEffect, useMemo, useState } from 'react';

import { Align, Box, Button, ButtonVariant, Justify, View } from '@bytel/trilogy-react';

import { useMediaQuery } from '@hooks/use-media-query';

import {
    FAI_COMPARATOR_DESKTOP_LIMIT,
    FAI_COMPARATOR_MOBILE_LIMIT,
    FAI_COMPARATOR_SELECTION_BREAKPOINT,
    FaiComparatorSelectedOffer,
    FaiComparatorSelectionEventDetail,
    getStoredFaiComparatorSelection,
    sendComparatorButtonTracking,
} from '@helpers/fai-comparator';
import { paths } from '@helpers/path';

import { CUSTOM_EVENTS } from '@constants/provider';

interface EligibilityComparatorStickyBarProps {
    showComparatorSelection: boolean;
}

export function EligibilityComparatorStickyBar({
    showComparatorSelection,
}: Readonly<EligibilityComparatorStickyBarProps>) {
    const [selectedOffers, setSelectedOffers] = useState<FaiComparatorSelectedOffer[]>([]);
    const maxComparatorSelection = useMediaQuery(FAI_COMPARATOR_SELECTION_BREAKPOINT)
        ? FAI_COMPARATOR_MOBILE_LIMIT
        : FAI_COMPARATOR_DESKTOP_LIMIT;

    useEffect(() => {
        // Position omnikit widget just before the eligibility comparator sticky bar
        window.omnikit = { insertBeforeId: 'sticky-bar' };
    }, []);

    const handleSelectionChange = (event: Event) => {
        const { detail } = event as CustomEvent<FaiComparatorSelectionEventDetail>;
        setSelectedOffers(detail.selectedOffers);
    };

    useEffect(() => {
        if (!showComparatorSelection) {
            return;
        }

        window.addEventListener(CUSTOM_EVENTS.FAI_COMPARATOR_SELECTION, handleSelectionChange as EventListener);
        setSelectedOffers(getStoredFaiComparatorSelection());

        return () => {
            window.removeEventListener(CUSTOM_EVENTS.FAI_COMPARATOR_SELECTION, handleSelectionChange as EventListener);
        };
    }, [showComparatorSelection]);

    const comparatorUrl = useMemo(() => {
        if (!selectedOffers.length) {
            return paths.offersComparator;
        }

        const params = new URLSearchParams();
        const ranges = selectedOffers.map((offer) => `${offer.rangeNg}:${offer.commercialSeries}`);

        if (ranges.length) {
            params.set('ranges', ranges.join(','));
        }

        return `${paths.offersComparator}?${params.toString()}`;
    }, [selectedOffers]);

    if (!showComparatorSelection || selectedOffers.length === 0) {
        return null;
    }

    return (
        <Box className='is-radiusless is-sticky-bottom has-text-centered is-block is-marginless' id='sticky-bar'>
            <View flexable justify={Justify.CENTER} align={Align.CENTER} className='is-flex-wrap-nowrap buttons'>
                <Button
                    variant={ButtonVariant.SECONDARY}
                    href={comparatorUrl}
                    onClick={() => sendComparatorButtonTracking(selectedOffers)}
                >
                    {`Comparer ces box (${Math.min(maxComparatorSelection, selectedOffers.length)}/${maxComparatorSelection})`}
                </Button>
            </View>
        </Box>
    );
}
